import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SVGGroup } from '@pff-consumer/utils/lib/svg/svg-group'
import { colors } from '@pff-consumer/core-ui'
import { DEFAULT_OPTION_VALUES, DropdownInteractiveKeys } from '@pff-consumer/schema'
import { useFloating, flip, autoUpdate, offset } from '@floating-ui/react'
import { Icon } from '../../atoms/icon/icon.web'
import { ListOption } from '../../organisms/list/list-option/list-option.type'
import { DropdownProps } from './dropdown.props'
import { useStyles } from './dropdown.web.styles'
import { DropdownOption } from './dropdown-option/dropdown-option.web'
import { findIndexOfOption } from '../../utils/find-index-of-option'
import { getKeyDownInfo } from '../../utils/get-key-down-info'
import { useDropdownState } from '../../hooks/use-dropdown-state.web'

const SPACE_BETWEEN_DROPDOWN_BUTTON_AND_OPTIONS = 2

export const Dropdown = (props: DropdownProps) => {
  const {
    id,
    options,
    placeholder = '',
    onSelectOption,
    onBlur,
    onFocus,
    defaultOption = DEFAULT_OPTION_VALUES,
    iconProps = { iconName: 'triangle-down', iconColor: colors.dropdownIconColor },
    disabled = false,
    isOpen = false,
    isError = false,
    isReadOnly = false,
    containerStyles = '',
    dropdownButtonStyles = '',
    dropdownModalStyles = '',
    dataRecurly,
    renderOption,
  } = props

  const { iconName, iconColor } = iconProps

  const isAccessible = !isReadOnly && !disabled
  const [activeOption, setActiveOption] = useState(defaultOption)
  const [displayText, setDisplayText] = useState('')
  const selectedItemRef = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (defaultOption) {
      setActiveOption(defaultOption)
      setDisplayText(defaultOption.text || placeholder)
    }
  }, [placeholder, defaultOption])

  const isOptionSelected = !!activeOption.text && activeOption.text.trim() !== ''

  const {
    isModalOpen,
    focusedOptionIndex,
    handleAlphanumericKeyPress,
    onClickHandler,
    toggleModal,
    handleOpen,
    handleClose,
    moveFocusDown,
    moveFocusUp,
    setFocusedOptionIndex,
  } = useDropdownState({ options, isAccessible, isOpen, activeValue: activeOption.value })

  const styles = useStyles({ isError, isOptionSelected })

  // handle dropdown placement on page
  const { refs, floatingStyles } = useFloating({
    transform: false,
    middleware: [
      flip({
        fallbackPlacements: ['top'],
      }),
      offset(SPACE_BETWEEN_DROPDOWN_BUTTON_AND_OPTIONS),
    ],
    whileElementsMounted: autoUpdate,
  })

  const dropdownRef = useRef<HTMLDivElement>(null)
  const dropdownButtonRef = useRef<HTMLButtonElement>(null)
  const dropdownOptionsRef = useRef<HTMLUListElement>(null)

  // dynamic styles
  const containerElementStyles = `${styles.container} ${disabled ? styles.disabledState : ''} ${containerStyles}`
  const dropdownButtonElementStyles = `${styles.selectButton} ${dropdownButtonStyles}`
  const dropdownModalStyle = `${styles.selectDropdownModal} ${isModalOpen ? styles.selectDropdownModalOpen : ''} ${dropdownModalStyles}`

  const handleSelectOptions = useCallback(
    (selectedDropdownOption: ListOption) => {
      const { value: selectedValue, text: selectedText = '' } = selectedDropdownOption
      toggleModal(false)
      const selectedValueIndex = findIndexOfOption(options, selectedValue)
      setFocusedOptionIndex(selectedValueIndex)
      // Reject handle if there is no value change
      if (activeOption.value === selectedValue) {
        return
      }

      setActiveOption(selectedDropdownOption)
      setDisplayText(selectedText)

      onSelectOption(selectedDropdownOption)
    },
    [activeOption, onSelectOption, options, setFocusedOptionIndex, toggleModal]
  )

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (isModalOpen && dropdownRef.current && !dropdownRef?.current?.contains(e.target as Node)) {
        handleClose()
      }
    },
    [dropdownRef, isModalOpen, handleClose]
  )

  const onKeyDownHandler = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === DropdownInteractiveKeys.TAB) {
        // Don't prevent default unless you need custom behavior
        return
      }
      e.preventDefault()
      const { key } = e
      const currentFocusedOption = options[focusedOptionIndex]
      const { isAlphaNumericKey, canOpenUsingKey } = getKeyDownInfo(key, isModalOpen)
      if (isAlphaNumericKey) {
        handleAlphanumericKeyPress(key)
      }
      if (canOpenUsingKey) {
        handleOpen()
      } else if (isModalOpen) {
        switch (key) {
          case DropdownInteractiveKeys.ESCAPE:
            handleClose()
            break
          case DropdownInteractiveKeys.ARROW_DOWN:
            moveFocusDown()
            break
          case DropdownInteractiveKeys.ARROW_UP:
            moveFocusUp()
            break
          case DropdownInteractiveKeys.ENTER:
          case DropdownInteractiveKeys.SPACE:
            handleSelectOptions(currentFocusedOption)
            break
          default:
            break
        }
      }
    },
    [
      isModalOpen,
      handleSelectOptions,
      moveFocusDown,
      moveFocusUp,
      focusedOptionIndex,
      options,
      handleClose,
      handleOpen,
      handleAlphanumericKeyPress,
    ]
  )

  useEffect(() => {
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    // setting refs for floating-ui to work
    refs.setFloating(dropdownOptionsRef.current)
    refs.setReference(dropdownButtonRef.current)
  }, [refs])

  // Scroll to selected item when dropdown opens
  useEffect(() => {
    if (isModalOpen && selectedItemRef.current && dropdownOptionsRef.current) {
      const dropdownOptionsRect = dropdownOptionsRef.current.getBoundingClientRect()
      const selectedItemRect = selectedItemRef.current.getBoundingClientRect()
      // Calculate the scroll position to center the selected item
      const currentScrolledDistance = dropdownOptionsRef.current.scrollTop
      const scrollTop =
        selectedItemRect.top -
        dropdownOptionsRect.top -
        dropdownOptionsRect.height / 2 +
        selectedItemRect.height / 2 +
        currentScrolledDistance

      dropdownOptionsRef.current.scrollTop = Math.max(0, scrollTop)
    }
  }, [isModalOpen, focusedOptionIndex])

  return (
    <div
      className={containerElementStyles}
      ref={dropdownRef}
    >
      <button
        type='button'
        id={id}
        className={dropdownButtonElementStyles}
        role='combobox'
        aria-label='select button'
        aria-haspopup='listbox'
        aria-expanded={isModalOpen}
        aria-controls='select-dropdown'
        aria-invalid={isError}
        onClick={onClickHandler}
        onKeyDown={onKeyDownHandler}
        onBlur={onBlur}
        onFocus={onFocus}
        ref={dropdownButtonRef}
        data-testid={`dropdown.button.${id}`}
        disabled={disabled}
      >
        <input
          data-recurly={dataRecurly}
          value={displayText}
          data-testid='dropdown.displayText'
          className={styles.input}
          readOnly
          tabIndex={-1}
        />
        <Icon
          className={isModalOpen ? styles.arrowIconUp : styles.arrowIcon}
          group={SVGGroup.Icons}
          name={iconName}
          testID='dropdown.icon'
          height={14}
          width={14}
          fill={iconColor}
        />
      </button>
      <ul
        className={dropdownModalStyle}
        style={floatingStyles}
        role='listbox'
        id='select-dropdown'
        aria-labelledby='dropdown-button'
        ref={dropdownOptionsRef}
        data-testid='dropdown.modal.options'
      >
        {options.map((option: ListOption, index: number) => {
          const isFocusedOption = focusedOptionIndex === index
          return (
            <DropdownOption
              ref={isFocusedOption ? selectedItemRef : null}
              key={option.value}
              option={option}
              isSelected={activeOption.value === option.value}
              id={option.value}
              isFocused={isFocusedOption}
              renderOption={renderOption}
              onSelectOption={handleSelectOptions}
            />
          )
        })}
      </ul>
    </div>
  )
}
